import React from "react";
import { Icon } from "../core/Icon.jsx";

/* Menu — action/dropdown menu on a native anchored popover (no popper.js).
   Roving focus, Esc + click-away to close, role="menu"/"menuitem". Replaces the
   legacy MenuList/Popper/Grow/Paper composition (REG-378 part 3, FR-002).

   Usage:
     <Menu trigger={<IconButton icon="moreHorizontal" label="Actions" />}
           items={[{ id, label, icon?, onSelect, disabled? }]} /> */

export function Menu({ trigger, items = [], placement = "bottom-start", align, style, ...rest }) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const itemRefs = React.useRef([]);
  const enabledIndexes = items.map((it, i) => (it.disabled ? -1 : i)).filter((i) => i >= 0);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  React.useEffect(() => {
    if (open && enabledIndexes.length) {
      const first = itemRefs.current[enabledIndexes[0]];
      if (first) first.focus();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const focusByOffset = (current, offset) => {
    const pos = enabledIndexes.indexOf(current);
    const next = enabledIndexes[(pos + offset + enabledIndexes.length) % enabledIndexes.length];
    const el = itemRefs.current[next];
    if (el) el.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      focusByOffset(Number(e.target.dataset.index), 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusByOffset(Number(e.target.dataset.index), -1);
    }
  };

  const select = (item) => {
    if (item.disabled) return;
    setOpen(false);
    if (item.onSelect) item.onSelect();
  };

  const side = placement.startsWith("top") ? { bottom: "100%", marginBottom: 4 } : { top: "100%", marginTop: 4 };
  const horiz = (align || (placement.endsWith("end") ? "end" : "start")) === "end" ? { right: 0 } : { left: 0 };

  // The trigger may be a render function so call sites can reflect the open
  // state (e.g. rotate a chevron, highlight the active menu).
  const triggerNode = typeof trigger === "function" ? trigger(open) : trigger;

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-flex", ...style }} {...rest}>
      <span
        role="button"
        tabIndex={-1}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{ display: "inline-flex" }}
      >
        {triggerNode}
      </span>
      {open ? (
        <div
          role="menu"
          onKeyDown={onKeyDown}
          style={{
            position: "absolute",
            ...side,
            ...horiz,
            minWidth: 180,
            zIndex: 1000,
            background: "var(--surface-card)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-4)",
            padding: 4,
          }}
        >
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              data-index={i}
              data-testid={item.testId}
              className={item.className}
              disabled={item.disabled}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              onClick={() => select(item)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 10px",
                border: "none",
                background: "transparent",
                borderRadius: "var(--radius-sm)",
                color: item.disabled ? "var(--text-muted)" : "var(--text-body)",
                fontFamily: "var(--font-sans)",
                fontWeight: 500,
                fontSize: 14,
                textAlign: "left",
                cursor: item.disabled ? "default" : "pointer",
              }}
            >
              {item.icon ? <Icon name={item.icon} size={16} /> : null}
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default Menu;
